import React, { useState, useEffect } from 'react';
import { ICategory } from '../types/damTypes';

const getParentCategoryId = (category: ICategory): string | null => {
  const parent = category.parentCategory;
  if (typeof parent === 'string') return parent;
  if (parent && typeof parent === 'object') return parent._id || null;
  return null;
};

const findRootCategoryIdByName = (allCategories: ICategory[], name: string): string | null => {
  const normalizedName = name.trim().toLowerCase();
  const exactRoot = allCategories.find(
    category => (category.name || '').trim().toLowerCase() === normalizedName && !getParentCategoryId(category),
  );
  const fallback = allCategories.find(
    category => (category.name || '').trim().toLowerCase() === normalizedName,
  );
  return exactRoot?._id || fallback?._id || null;
};

export function useCategoryContext(
  allCategories: ICategory[],
  activeCategoryIds: string[]
) {
  // Shared category lookup maps
  const categoryById = React.useMemo(() => {
    const m = new Map<string, ICategory>();
    for (const c of allCategories) if (c?._id) m.set(c._id, c);
    return m;
  }, [allCategories]);

  const categoryParentById = React.useMemo(() => {
    const m = new Map<string, string | null>();
    for (const c of allCategories) {
      if (!c?._id) continue;
      m.set(c._id, getParentCategoryId(c));
    }
    return m;
  }, [allCategories]);

  // ----- Butiksbilder (Store photos) level-3 filter -----
  const [storeTypeCategoryId, setStoreTypeCategoryId] = useState<string>('');

  const storeImagesRootId = React.useMemo(() => {
    const node = allCategories.find(c => (c.name || '').toLowerCase() === 'butiksbilder');
    return node?._id || null;
  }, [allCategories]);

  const isUnderStoreImages = React.useMemo(() => {
    if (!storeImagesRootId) return false;
    if (!activeCategoryIds || activeCategoryIds.length === 0) return false;
    const isDescendant = (id: string): boolean => {
      let current: string | null | undefined = id;
      while (current) {
        if (current === storeImagesRootId) return true;
        current = categoryParentById.get(current) || null;
      }
      return false;
    };
    return activeCategoryIds.some(id => id === storeImagesRootId || isDescendant(id));
  }, [activeCategoryIds, storeImagesRootId, categoryParentById]);

  const selectedStoreChains = React.useMemo(() => {
    if (!storeImagesRootId) return [] as string[];
    const chainIds = new Set<string>();
    categoryParentById.forEach((pid, id) => {
      if (pid === storeImagesRootId) chainIds.add(id);
    });
    return (activeCategoryIds || []).filter(id => chainIds.has(id));
  }, [categoryParentById, activeCategoryIds, storeImagesRootId]);

  const storeTypeOptions = React.useMemo(() => {
    if (!storeImagesRootId) return [] as ICategory[];
    if (!selectedStoreChains || selectedStoreChains.length === 0) return [] as ICategory[];
    const chainSet = new Set(selectedStoreChains);
    const res: ICategory[] = [];
    const seen = new Set<string>();
    categoryParentById.forEach((pid, id) => {
      if (pid && chainSet.has(pid) && !seen.has(id)) {
        seen.add(id);
        const cat = categoryById.get(id);
        if (cat) res.push(cat);
      }
    });
    return res.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'sv'));
  }, [categoryParentById, categoryById, selectedStoreChains, storeImagesRootId]);

  // Reset store-type filter when leaving Butiksbilder context or when chain selection changes
  useEffect(() => {
    if (!isUnderStoreImages) {
      if (storeTypeCategoryId) setStoreTypeCategoryId('');
      return;
    }
    if (storeTypeCategoryId && !storeTypeOptions.some(c => c._id === storeTypeCategoryId)) {
      setStoreTypeCategoryId('');
    }
  }, [isUnderStoreImages, storeTypeOptions, storeTypeCategoryId]);

  // ----- Produkter -----
  const productsRootId = React.useMemo(() => {
    return findRootCategoryIdByName(allCategories, 'produkter');
  }, [allCategories]);

  const isUnderProducts = React.useMemo(() => {
    if (!productsRootId) return false;
    if (!activeCategoryIds || activeCategoryIds.length === 0) return false;
    const isDescendant = (id: string): boolean => {
      let current: string | null | undefined = id;
      while (current) {
        if (current === productsRootId) return true;
        current = categoryParentById.get(current) || null;
      }
      return false;
    };
    return activeCategoryIds.some(id => id === productsRootId || isDescendant(id));
  }, [activeCategoryIds, productsRootId, categoryParentById]);

  // ----- Vin -----
  const wineRootId = React.useMemo(() => {
    return findRootCategoryIdByName(allCategories, 'vin');
  }, [allCategories]);

  const isUnderWine = React.useMemo(() => {
    if (!wineRootId) return false;
    if (!activeCategoryIds || activeCategoryIds.length === 0) return false;
    const isDescendant = (id: string): boolean => {
      let current: string | null | undefined = id;
      while (current) {
        if (current === wineRootId) return true;
        current = categoryParentById.get(current) || null;
      }
      return false;
    };
    return activeCategoryIds.some(id => id === wineRootId || isDescendant(id));
  }, [activeCategoryIds, wineRootId, categoryParentById]);

  // ----- Recept -----
  const recipesRootId = React.useMemo(() => {
    return findRootCategoryIdByName(allCategories, 'recept');
  }, [allCategories]);

  const isUnderRecipes = React.useMemo(() => {
    if (!recipesRootId) return false;
    if (!activeCategoryIds || activeCategoryIds.length === 0) return false;
    const isDescendant = (id: string): boolean => {
      let current: string | null | undefined = id;
      while (current) {
        if (current === recipesRootId) return true;
        current = categoryParentById.get(current) || null;
      }
      return false;
    };
    return activeCategoryIds.some(id => id === recipesRootId || isDescendant(id));
  }, [activeCategoryIds, recipesRootId, categoryParentById]);

  // Badge visibility on product cards: always show every badge the asset has.
  // The visibleInAll / visibleInCategories flags only control the filter bar (BadgeFilter),
  // not the badges rendered on individual asset cards.
  const isBadgeVisibleInCurrentView = (_badge: any): boolean => {
    return true;
  };

  return {
    categoryById,
    categoryParentById,
    storeTypeCategoryId,
    setStoreTypeCategoryId,
    storeImagesRootId,
    isUnderStoreImages,
    selectedStoreChains,
    storeTypeOptions,
    productsRootId,
    isUnderProducts,
    wineRootId,
    isUnderWine,
    recipesRootId,
    isUnderRecipes,
    isBadgeVisibleInCurrentView,
  };
}
